/**
 * Status → public-visibility data for the venues admin (single source of truth,
 * shared by the list-page guide + the detail-page banner).
 *
 * Mirrors the API: `VenueService.listPublic` shows only `status='active'`;
 * `getPublicDetail` 404s draft/suspended, shows a "closed" notice for closed,
 * and redirects merged → canonical. Delete is allowed only for draft/suspended.
 */
export type VenueVisibilityTone = 'success' | 'warning' | 'neutral' | 'danger' | 'info';

export interface VenueStatusRow {
  readonly status: string;
  readonly tone: VenueVisibilityTone;
  readonly isPublic: boolean;
  /** i18n key for the description. */
  readonly key: string;
  /** English default for the description. */
  readonly def: string;
}

export const VENUE_STATUS_ROWS: readonly VenueStatusRow[] = [
  {
    status: 'active',
    tone: 'success',
    isPublic: true,
    key: 'admin:venue.f42.visibility.active',
    def: 'Appears in search, maps & discovery for all users (and is bookable once claimed + its profile is complete).',
  },
  {
    status: 'draft',
    tone: 'neutral',
    isPublic: false,
    key: 'admin:venue.f42.visibility.draft',
    def: 'Not published yet (admin-only); the public page returns 404.',
  },
  {
    status: 'suspended',
    tone: 'warning',
    isPublic: false,
    key: 'admin:venue.f42.visibility.suspended',
    def: 'Removed from search and its page 404s. New bookings are blocked; already-confirmed bookings are honoured.',
  },
  {
    status: 'closed',
    tone: 'warning',
    isPublic: false,
    key: 'admin:venue.f42.visibility.closed',
    def: 'Removed from search; the direct page shows a “permanently closed” notice (the URL still resolves).',
  },
  {
    status: 'merged',
    tone: 'info',
    isPublic: false,
    key: 'admin:venue.f42.visibility.merged',
    def: 'Its old URL redirects to the venue it was merged into.',
  },
];

/** Per-venue visibility summary for the detail-page header indicator. */
export function venueVisibility(status: string): VenueStatusRow {
  return (
    VENUE_STATUS_ROWS.find((x) => x.status === status) ?? {
      status,
      isPublic: false,
      tone: 'neutral',
      key: 'admin:venue.f42.visibility.unknown',
      def: 'Public visibility depends on this venue’s status.',
    }
  );
}

/** Semantic dot colour (Tailwind class) for the header visibility indicator. */
export function visibilityDotClass(row: VenueStatusRow): string {
  if (row.isPublic) return 'bg-success';
  if (row.tone === 'warning') return 'bg-warning';
  if (row.tone === 'info') return 'bg-info';
  return 'bg-ink-3';
}
